/**
 * Os tipos de local — cidade, ruína, masmorra — administrados de dentro de Locais.
 *
 * <p>Tipo saiu do menu porque ele não é uma ficha que alguém vai ler: existe só para
 * classificar local, e classificar local se faz em Locais. Mas tirar o item do menu
 * sem pôr isto no lugar deixaria renomear e excluir um tipo sem tela nenhuma — e um
 * tipo escrito errado ficaria errado para sempre.
 *
 * <p>Criar aqui é redundante com o "+ Criar" do seletor dentro da ficha do Local, e é
 * de propósito: quem já está escrevendo um local não deveria ter de vir aqui, e quem
 * está arrumando a casa não deveria ter de abrir um local para criar um tipo.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroApi } from '../../api/cliente';
import * as mundoApi from './mundoApi';
import type { Resumo } from './tipos';

export function GerenciadorDeTipos({ aoFechar }: { aoFechar: () => void }) {
  const fila = useQueryClient();
  const navegar = useNavigate();
  const [novo, setNovo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  /** O texto do 409, enquanto o time decide se exclui um tipo que está em uso. */
  const [confirmando, setConfirmando] = useState<{ id: number; texto: string } | null>(null);

  const { data: tipos, isLoading } = useQuery<Resumo[]>({
    queryKey: ['mundo', 'tipos'],
    queryFn: () => mundoApi.listar('tipos'),
  });

  function falhou(e: unknown) {
    setErro(e instanceof Error ? e.message : 'Falha na operação.');
  }

  /** Locais mostram o tipo; mexer num tipo muda o que eles exibem. */
  async function recarregar() {
    await fila.invalidateQueries({ queryKey: ['mundo'] });
  }

  const criar = useMutation({
    mutationFn: (nome: string) => mundoApi.criar('tipos', { nome }),
    onSuccess: async () => {
      setNovo('');
      setErro(null);
      await recarregar();
    },
    onError: falhou,
  });

  const renomear = useMutation({
    mutationFn: ({ id, nome }: { id: number; nome: string }) =>
      mundoApi.salvar('tipos', id, { nome }),
    onSuccess: () => void recarregar(),
    onError: falhou,
  });

  const excluir = useMutation({
    mutationFn: ({ id, confirmado }: { id: number; confirmado: boolean }) =>
      mundoApi.excluir('tipos', id, confirmado),
    onSuccess: async () => {
      setConfirmando(null);
      setErro(null);
      await recarregar();
    },
    // 409 não é falha: é a API dizendo quantos locais usam este tipo antes de deixar.
    onError: (e, variaveis) =>
      e instanceof ErroApi && e.status === 409
        ? setConfirmando({ id: variaveis.id, texto: e.message })
        : falhou(e),
  });

  return (
    <div className="fi-modal" onClick={aoFechar}>
      <div className="fi-modal-cx" onClick={(e) => e.stopPropagation()}>
        <div className="fi-modal-selo">Tipos de local</div>
        <h3>Como os locais são classificados</h3>
        <p className="fi-modal-nota">
          Cidade, ruína, masmorra. Um local pode ter mais de um, e é na ficha dele que se
          escolhe — aqui ficam os tipos que existem para escolher.
        </p>

        {isLoading && <p className="fi-modal-nota">Carregando…</p>}

        <div className="tp-lista">
          {(tipos ?? []).map((tipo) => (
            <div className="tp-linha" key={tipo.id}>
              <input
                defaultValue={tipo.nome}
                onBlur={(e) => {
                  const nome = e.target.value.trim();
                  if (nome && nome !== tipo.nome) renomear.mutate({ id: tipo.id, nome });
                  else e.target.value = tipo.nome;
                }}
              />
              {/* A descrição do tipo mora na ficha dele, que continua de pé mesmo sem
                  item no menu. */}
              <button
                type="button"
                className="tp-abrir"
                title="Abrir a ficha, para escrever a descrição"
                onClick={() => navegar(`/mundo/tipos/${tipo.id}`)}
              >
                ↗
              </button>
              <button
                type="button"
                className="tp-x"
                title="Excluir este tipo"
                onClick={() => excluir.mutate({ id: tipo.id, confirmado: false })}
              >
                ×
              </button>
            </div>
          ))}

          {!isLoading && (tipos ?? []).length === 0 && (
            <p className="fi-modal-nota">Nenhum tipo ainda.</p>
          )}
        </div>

        <div className="tp-novo">
          <input
            value={novo}
            placeholder="Novo tipo: taverna, cripta, porto…"
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && novo.trim()) criar.mutate(novo.trim());
            }}
          />
          <button
            type="button"
            disabled={!novo.trim() || criar.isPending}
            onClick={() => criar.mutate(novo.trim())}
          >
            + Criar
          </button>
        </div>

        {confirmando && (
          <div className="tp-confirma">
            <p>{confirmando.texto}</p>
            <div>
              <button
                type="button"
                className="perigo"
                onClick={() => excluir.mutate({ id: confirmando.id, confirmado: true })}
              >
                Excluir mesmo assim
              </button>
              <button type="button" onClick={() => setConfirmando(null)}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        {erro && <p className="tp-erro">{erro}</p>}

        <div className="fi-modal-btns">
          <button onClick={aoFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
